import type { RouteRecordRaw } from 'vue-router'
import { createMeta } from 'src/shared/lib/access'

const routes: RouteRecordRaw[] = [
  {
    path: '/auth',
    component: () => import('src/pages/LoginPage.vue'),
  },
  {
    path: '/',
    component: () => import('src/pages/layouts/MainLayout.vue'),
    children: [
      {
        // Дашборд admin-only: подписан на evt.sbc.*/queue.*/security.*/health.*
        // — прав на них нет ни у operator, ни у supervisor, и без гарда они
        // получали пачку Permissions Violation прямо на посадочной странице.
        // Не-админа guard уводит на /pulse (см. router/index.ts).
        path: '',
        component: () => import('src/pages/DashboardPage.vue'),
        // Сводка из четырёх карточек — открыта, если читается хоть одна; каждая
        // карточка показывается по своему праву (DashboardPage).
        meta: {
          guard: createMeta({
            menu: 'menu:dashboard',
            capAnyOf: ['sbc.sipuser:read', 'sbc.trunk:read', 'queue:read', 'strategy.services:view'],
            role: 'admin',
          }),
        },
      },
      {
        // Рабочее место оператора — хост коммуникационной панели (CPANEL Ф5).
        path: 'workplace',
        component: () => import('src/pages/WorkplacePage.vue'),
        meta: { guard: createMeta({ menu: 'menu:workplace', capAnyOf: ['workplace:softphone', 'chat.message:history'], roleAnyOf: ['operator', 'supervisor', 'admin'] }) },
      },
      {
        // Без роли в мете: до загрузки прав Пульс открыт всем, как до эпика ACCESS;
        // кастомная роль без «Пульс» его не видит.
        path: 'pulse',
        component: () => import('src/pages/PulsePage.vue'),
        meta: { guard: createMeta({ menu: 'menu:pulse', capAnyOf: ['pulse:view'] }) },
      },
      {
        path: 'sip-users',
        component: () => import('src/pages/SipUsersPage.vue'),
        meta: { guard: createMeta({ menu: 'menu:sip_users', capAnyOf: ['sbc.sipuser:read'], role: 'admin' }) },
      },
      {
        path: 'trunks',
        component: () => import('src/pages/TrunksPage.vue'),
        meta: { guard: createMeta({ menu: 'menu:trunks', capAnyOf: ['sbc.trunk:read'], role: 'admin' }) },
      },
      {
        path: 'queues',
        component: () => import('src/pages/QueuesPage.vue'),
        meta: { guard: createMeta({ menu: 'menu:queues', capAnyOf: ['queue:read'], role: 'admin' }) },
      },
      {
        // Справочник результатов обработки сессии (эпик CPANEL, шаг 23).
        path: 'results',
        component: () => import('src/pages/ResultsPage.vue'),
        meta: { guard: createMeta({ menu: 'menu:results', capAnyOf: ['result:read'], role: 'admin' }) },
      },
      {
        // Активные звонки = `q.chain.calls.list` — его даёт только «Пульс → мониторинг».
        // Без гарда страница была открыта любому вошедшему и молча пустела (ACCESS).
        path: 'calls',
        component: () => import('src/pages/CallsPage.vue'),
        meta: { guard: createMeta({ menu: 'menu:calls', capAnyOf: ['pulse:view'], roleAnyOf: ['operator', 'supervisor', 'admin'] }) },
      },
      {
        // История звонков: admin + supervisor (read-only, права на q.cdr.*).
        path: 'cdr',
        component: () => import('src/pages/CdrPage.vue'),
        meta: { guard: createMeta({ menu: 'menu:cdr', capAnyOf: ['calls:history'], roleAnyOf: ['admin', 'supervisor'] }) },
      },
      {
        path: 'callroutes',
        component: () => import('src/pages/CallRoutesPage.vue'),
        meta: { guard: createMeta({ menu: 'menu:callroutes', capAnyOf: ['callroute:read'], role: 'admin' }) },
      },
      {
        path: 'redirect-rules',
        component: () => import('src/pages/RedirectRulesPage.vue'),
        meta: { guard: createMeta({ menu: 'menu:redirect_rules', capAnyOf: ['redirect:read'], role: 'admin' }) },
      },
      {
        path: 'ring-groups',
        component: () => import('src/pages/RingGroupsPage.vue'),
        meta: { guard: createMeta({ menu: 'menu:ring_groups', capAnyOf: ['ringgroup:read'], role: 'admin' }) },
      },
      {
        path: 'trunk-groups',
        component: () => import('src/pages/TrunkGroupsPage.vue'),
        meta: { guard: createMeta({ menu: 'menu:trunk_groups', capAnyOf: ['trunkgroup:read'], role: 'admin' }) },
      },
      {
        path: 'customers',
        component: () => import('src/pages/CustomersPage.vue'),
        meta: { guard: createMeta({ menu: 'menu:customers', capAnyOf: ['customer:read'], role: 'admin' }) },
      },
      {
        // Шаблоны сообщений: supervisor — чтение, admin — CRUD (контролы скрыты по роли).
        path: 'msgtemplates',
        component: () => import('src/pages/MsgTemplatesPage.vue'),
        meta: { guard: createMeta({ menu: 'menu:msgtemplates', capAnyOf: ['msgtemplate:read'], roleAnyOf: ['admin', 'supervisor'] }) },
      },
      {
        // Журнал администратора: один markdown-документ на тенант (только admin).
        path: 'journal',
        component: () => import('src/pages/JournalPage.vue'),
        meta: { guard: createMeta({ menu: 'menu:journal', capAnyOf: ['journal:read'], role: 'admin' }) },
      },
      {
        // Справочник сценариев (SCENCAT): объявленные стратегиями + ручные; CRUD ручных — по капабилити.
        path: 'scenarios',
        component: () => import('src/pages/ScenariosPage.vue'),
        meta: { guard: createMeta({ menu: 'menu:scenarios', roleAnyOf: ['admin', 'supervisor'] }) },
      },
      {
        // Массовый импорт сессий из Excel/CSV (cmd.session.import).
        // supervisor — полный доступ к разделу наравне с admin.
        path: 'import-sessions',
        component: () => import('src/pages/ImportSessionsPage.vue'),
        meta: { guard: createMeta({ menu: 'menu:import_sessions', capAnyOf: ['session:import'], roleAnyOf: ['admin', 'supervisor'] }) },
      },
      {
        // Подписан на evt.security.* — admin-only (см. комментарий у дашборда).
        // Только system: блокировки общие на всю установку (адреса всех
        // организаций), сервер отказывает прочим тенантам ('forbidden').
        path: 'security',
        component: () => import('src/pages/SecurityPage.vue'),
        meta: { guard: createMeta({ tenant: 'system', role: 'admin' }) },
      },
      {
        // Чаты: список диалогов оператора (Ф5.3). Окно переписки — Ф5.4.
        path: 'chats',
        component: () => import('src/pages/ChatsPage.vue'),
        meta: { guard: createMeta({ menu: 'menu:chats', capAnyOf: ['chat.message:history'], roleAnyOf: ['operator', 'supervisor', 'admin'] }) },
      },
      {
        // Провайдеры чатов (эпик CHAT, Ф5.2): аккаунты + каналы, креды — admin-only.
        path: 'chat-providers',
        component: () => import('src/pages/ChatProvidersPage.vue'),
        meta: { guard: createMeta({ menu: 'menu:chat_providers', capAnyOf: ['chat.account:read'], role: 'admin' }) },
      },
      {
        // Журнал чат-сообщений тенанта (перенос vq-messages-page из ЭРЫ, Ф5.6).
        path: 'chat-messages',
        component: () => import('src/pages/ChatMessagesPage.vue'),
        meta: { guard: createMeta({ menu: 'menu:chat_messages', capAllOf: ['chat.message:history', 'customer:search'], roleAnyOf: ['admin', 'supervisor'] }) },
      },
      {
        path: 'tenants',
        component: () => import('src/pages/TenantsPage.vue'),
        meta: { guard: createMeta({ tenant: 'system', role: 'admin' }) },
      },
      {
        // Роли тенанта (эпик ACCESS): редактор галочек, роли людей и стратегий.
        path: 'roles',
        component: () => import('src/pages/RolesPage.vue'),
        meta: { guard: createMeta({ menu: 'menu:roles', capAnyOf: ['access.role:read'], role: 'admin', notTenant: 'system' }) },
      },
      {
        path: 'users',
        component: () => import('src/pages/UsersPage.vue'),
        meta: { guard: createMeta({ menu: 'menu:users', capAnyOf: ['user:read'], role: 'admin' }) },
      },
      {
        path: 'tokens',
        component: () => import('src/pages/TokensPage.vue'),
        meta: { guard: createMeta({ menu: 'menu:tokens', capAnyOf: ['token:read'], role: 'admin' }) },
      },
      {
        path: 'health',
        component: () => import('src/pages/AdminHealthPage.vue'),
        meta: { guard: createMeta({ tenant: 'system', role: 'admin' }) },
      },
      {
        // Параметры подключения (эпик SYSINFO). Супервайзеру страница нужна не
        // меньше админа: именно он отвечает оператору «какой адрес вбить в
        // телефон». Паспорт инсталляции внутри страницы подгружается только
        // администратору установки — его гейтит сервер, не роут.
        path: 'connectivity',
        component: () => import('src/pages/SystemConnectivityPage.vue'),
        meta: { guard: createMeta({ menu: 'menu:connectivity', capAnyOf: ['system:connectivity'], roleAnyOf: ['admin', 'supervisor'] }) },
      },
      {
        path: 'strategy-files',
        component: () => import('src/pages/StrategyFilesPage.vue'),
        meta: { guard: createMeta({ menu: 'menu:strategy_files', capAnyOf: ['media.file:read'], role: 'admin' }) },
      },
      {
        path: 'strategy-env',
        component: () => import('src/pages/StrategyEnvPage.vue'),
        meta: { guard: createMeta({ menu: 'menu:strategy_env', capAnyOf: ['strategyenv:read'], role: 'admin' }) },
      },
      {
        // Мониторинг heartbeat'ов стратегий — tenant-scoped через изоляцию NATS-аккаунтов.
        // НЕ tenant:'system' (как /health): любой admin видит стратегии СВОЕГО тенанта.
        path: 'strategy-heartbeats',
        component: () => import('src/pages/StrategyHeartbeatsPage.vue'),
        meta: { guard: createMeta({ menu: 'menu:strategy_heartbeats', capAnyOf: ['strategy.services:view', 'sandbox.strategy:read'], role: 'admin' }) },
      },
      {
        path: 'speech-providers',
        component: () => import('src/pages/SpeechProvidersPage.vue'),
        // Вкладки — по своим правам: провайдеры и кэш синтеза речи.
        meta: { guard: createMeta({ menu: 'menu:speech_providers', capAnyOf: ['spg.provider:read', 'spg.tts-cache:read'], role: 'admin' }) },
      },
      {
        // Профили хранения записей (S3, эпик RECSTOR) — admin-only, креды в форме.
        path: 'recording-storages',
        component: () => import('src/pages/RecordingStoragesPage.vue'),
        meta: { guard: createMeta({ menu: 'menu:recording_storages', capAnyOf: ['recording.storage:read'], role: 'admin' }) },
      },
      {
        // Несведённые записи (DLQ) — почему запись не доехала до S3-профиля.
        path: 'recording-staging',
        component: () => import('src/pages/RecordingStagingPage.vue'),
        meta: { guard: createMeta({ menu: 'menu:recording_staging', capAnyOf: ['recordings:dlq'], role: 'admin' }) },
      },
      {
        // Транскрибация: supervisor — чтение транскриптов, admin — + провайдеры и запуск.
        path: 'transcription',
        component: () => import('src/pages/TranscriptionPage.vue'),
        // Две вкладки — по своим правам: транскрипты и сервисы расшифровки (провайдеры).
        meta: {
          guard: createMeta({
            menu: 'menu:transcription',
            capAnyOf: ['transcription.transcript:read', 'transcription.provider:read'],
            roleAnyOf: ['admin', 'supervisor'],
          }),
        },
      },
      { path: ':catchAll(.*)*', component: () => import('src/pages/ErrorNotFound.vue') },
    ],
  },
]

export default routes
